import { call, put, select, spawn } from 'redux-saga/effects';

import { GeneratorResult, selectTyped, takeEvery } from '@pressreader/appstore';
import { loginFlowSaga } from '@pressreader/authentication-utils';
import { openCommentsDialogAndGetArticleCommentsMetadata, type OpenCommentsDialogAndWaitForResultOptions } from '@pressreader/comments-popups';
import { ArticleFields, type IArticle, type IArticleUpdatable, type IIssueInfo } from '@pressreader/content-types';
import { extractCidDateKeyString, isIssueKnownUnavailable } from '@pressreader/content-utils';
import { error } from '@pressreader/logger';

import { selectArticleFeaturesConfig } from '../../global.selectors';
import { getArticleIdForSocialMetadataOps, logErrorArticlePrimaryIdMissing } from '../../utils';
import {
    articleCommentsMetadataUpdated,
    articleCommentsOpenDialogAndPublishCommentsMetadataAction,
    articlesLoaded,
    articleSocialMetaUpdated,
    articlesUpdated,
    articleUpdated,
    commentRequestAction,
    loadArticleDone,
} from '../actions';
import {
    getArticleSocialMetadata,
    loadArticleById,
    loadArticlePrimaryIds,
    loadArticlesSocialMetadata,
    loadSocialMetadataForArticle,
} from '../saga.utils';
import { selectArticleById, selectArticleIdentityById } from '../selectors';

function* isArticleSocialEnabled() {
    const { articleSocialEnabled } = yield* selectTyped(selectArticleFeaturesConfig);

    return articleSocialEnabled;
}

function* openCommentsDialogForArticleAndPublishResults(
    commentsDialogOptions: Omit<OpenCommentsDialogAndWaitForResultOptions, 'issueCidDateKey'> & {
        primaryArticleId: string;
        issueCidDateKey?: IIssueInfo['issueCidDateKey'];
    },
): GeneratorResult<void> {
    const { articleId, primaryArticleId, issueCidDateKey } = commentsDialogOptions;

    const articleIdOpenDialogFor = getArticleIdForSocialMetadataOps({ id: articleId, primaryArticleId });
    let issueCidDateKeyRes = issueCidDateKey;
    if (!issueCidDateKeyRes) {
        const article = yield* loadArticleById(articleIdOpenDialogFor, { requiredFields: ArticleFields.Issue | ArticleFields.Publication });
        if (!article) {
            const err = new Error(`Article is not found by id: ${articleIdOpenDialogFor}`);
            error(err);
        } else {
            const articleIssueKey = article?.issue?.issueKey;
            if (!articleIssueKey && !isIssueKnownUnavailable(article)) {
                const err = new Error('Article issue key is missing');
                error(err);
            } else if (articleIssueKey) {
                issueCidDateKeyRes = extractCidDateKeyString(articleIssueKey);
            }
        }
    }

    const openDialogResult = yield* openCommentsDialogAndGetArticleCommentsMetadata({
        articleId: articleIdOpenDialogFor,
        issueCidDateKey: issueCidDateKeyRes,
    });

    if (!openDialogResult) {
        return;
    }

    const {
        commentsMetadata: { totalCommentsCount, articleId: articleIdCommentsAddedFor },
    } = openDialogResult;

    yield put(
        articleCommentsMetadataUpdated({
            articleId: articleIdCommentsAddedFor,
            commentsMetadata: {
                totalCount: totalCommentsCount,
            },
        }),
    );
}

function* onArticleCommentsOpenDialogAndPublishCommentsMetadataAction({
    payload,
}: ReturnType<typeof articleCommentsOpenDialogAndPublishCommentsMetadataAction>): GeneratorResult<void> {
    const { articleId, primaryArticleId } = payload;
    let primaryArticleIdRes = primaryArticleId;

    if (!primaryArticleId) {
        const [{ primaryArticleId }] = yield* loadArticlePrimaryIds([articleId]);
        primaryArticleIdRes = primaryArticleId;
    }

    if (!primaryArticleIdRes) {
        logErrorArticlePrimaryIdMissing(articleId);
        throw new Error(`Primary article id is missing for the article id: ${articleId}`);
    }

    yield call(openCommentsDialogForArticleAndPublishResults, {
        ...payload,
        primaryArticleId: primaryArticleIdRes,
    });
}

function* commentRequest({ payload }: ReturnType<typeof commentRequestAction>) {
    const { articleId, primaryArticleId } = payload;

    let article: IArticle | null = null;
    let primaryArticleIdRes: string | undefined = primaryArticleId;

    if (!primaryArticleId) {
        article = yield* loadArticleById(articleId, {
            requiredFields: ArticleFields.Issue | ArticleFields.Publication,
        });
        if (!article) {
            error(`Article is not found by id: ${articleId}`);
            throw new Error(`Article is not found by id: ${articleId}`);
        }
        primaryArticleIdRes = article.primaryArticleId;
    }

    if (!primaryArticleIdRes) {
        logErrorArticlePrimaryIdMissing(articleId);
        throw new Error(`Primary article id is missing for the article id: ${articleId}`);
    }

    const articleIdForSocialMeta = getArticleIdForSocialMetadataOps({ id: articleId, primaryArticleId: primaryArticleIdRes });

    article = yield* loadArticleById(articleIdForSocialMeta, {
        requiredFields: ArticleFields.Issue | ArticleFields.Publication,
    });

    if (!article) {
        error(`article.core.saga.commentRequest.article. Article with the id "${articleIdForSocialMeta}" has not been found`);
        throw new Error(`Article with the id "${articleIdForSocialMeta}" has not been found`);
    }
    if (!article.comments?.count) {
        // if there are no comments available, need to log in before writing a comment
        const result = yield* loginFlowSaga();
        if (!result) {
            return;
        }
    }

    const articleIssueKey = article.issue?.issueKey;
    yield call(openCommentsDialogForArticleAndPublishResults, {
        articleId: articleIdForSocialMeta,
        primaryArticleId: articleIdForSocialMeta,
        issueCidDateKey: articleIssueKey ? extractCidDateKeyString(articleIssueKey) : '',
    });
}

function* onArticleCommentsMetadataUpdated({ payload }: ReturnType<typeof articleCommentsMetadataUpdated>) {
    const {
        articleId,
        commentsMetadata: { totalCount },
    } = payload;

    const articleOrNull: ReturnType<typeof selectArticleById> = yield select(selectArticleById, { articleId });
    const articleUpdateObject: IArticleUpdatable = {
        comments: {
            ...articleOrNull?.comments,
            count: totalCount,
        },
    };

    yield put(
        articleUpdated({
            articleId,
            article: articleUpdateObject,
        }),
    );
}

function* onArticleUpdated({ payload }: ReturnType<typeof articleUpdated>) {
    const articleSocialEnabled = yield* isArticleSocialEnabled();

    if (!articleSocialEnabled) {
        return;
    }

    const { articleId, article: articleUpdate } = payload;
    const socialMetadataUpdate = getArticleSocialMetadata(articleUpdate);
    yield put(
        articleSocialMetaUpdated({
            articleSocialMetaUpdates: [
                {
                    articleId,
                    socialMetadataUpdate,
                },
            ],
        }),
    );
}

function* onMultipleArticlesUpdated({ payload }: ReturnType<typeof articlesUpdated>) {
    const articleSocialEnabled = yield* isArticleSocialEnabled();

    if (!articleSocialEnabled) {
        return;
    }

    const { articleUpdates } = payload;
    const articleSocialMetaUpdates = articleUpdates.map(articleUpdate => {
        const { articleId } = articleUpdate;
        const socialMetadataUpdate = getArticleSocialMetadata(articleUpdate);
        return {
            articleId,
            socialMetadataUpdate,
        };
    });

    yield put(
        articleSocialMetaUpdated({
            articleSocialMetaUpdates,
        }),
    );
}

function* onArticlesLoaded({ payload }: ReturnType<typeof articlesLoaded>): GeneratorResult<void> {
    const articleSocialEnabled = yield* isArticleSocialEnabled();

    if (!articleSocialEnabled) {
        return;
    }

    const { articles } = payload;
    const articleSocialMetaUpdates = [];

    for (const article of articles) {
        const { id: articleId, primaryArticleId } = article;
        const socialMetadataUpdate = getArticleSocialMetadata(article);

        let primaryArticleIdRes: string | undefined = primaryArticleId;
        if (!primaryArticleIdRes) {
            primaryArticleIdRes = (yield* selectTyped(selectArticleIdentityById, { articleId }))?.primaryArticleId;
        }

        if (!primaryArticleIdRes) {
            logErrorArticlePrimaryIdMissing(articleId);
            continue;
        }

        const articleIdForSocialMeta = getArticleIdForSocialMetadataOps({ id: articleId, primaryArticleId });

        articleSocialMetaUpdates.push({
            articleId: articleIdForSocialMeta,
            socialMetadataUpdate,
        });
    }

    yield put(
        articleSocialMetaUpdated({
            articleSocialMetaUpdates,
        }),
    );

    yield spawn(loadArticlesSocialMetadata, {
        articleIdsForSocialMeta: articles,
    });
}

function* onArticleLoaded({ payload }: ReturnType<typeof loadArticleDone>) {
    const articleSocialEnabled = yield* isArticleSocialEnabled();

    if (!articleSocialEnabled) {
        return;
    }

    const { article, requiredFields: loadedFields } = payload;
    const articleAvailableFields = article.availableFields | (loadedFields ?? ArticleFields.None);
    const articleIdForSocialMeta = getArticleIdForSocialMetadataOps(article);

    const socialMetadataUpdate = getArticleSocialMetadata(article);
    yield put(
        articleSocialMetaUpdated({
            articleSocialMetaUpdates: [
                {
                    articleId: articleIdForSocialMeta,
                    socialMetadataUpdate,
                },
            ],
        }),
    );

    yield spawn(loadSocialMetadataForArticle, {
        articleIdsForSocialMeta: article,
        loadedFields: articleAvailableFields,
        language: article.language ?? null,
    });
}

function* updatePrimaryArticleSocialMetadata({ payload }: ReturnType<typeof articleSocialMetaUpdated>) {
    const articleSocialEnabled = yield* isArticleSocialEnabled();

    if (!articleSocialEnabled) {
        return;
    }

    const { articleSocialMetaUpdates } = payload;
    const socialMetadataForPrimaryArticleUpdates = [];
    const articlePrimaryIdsMetadata = yield* loadArticlePrimaryIds(articleSocialMetaUpdates.map(({ articleId }) => articleId));

    for (const { articleId, primaryArticleId } of articlePrimaryIdsMetadata) {
        if (!primaryArticleId) {
            logErrorArticlePrimaryIdMissing(articleId);
            continue;
        }

        const articleIdForSocialMetaOps = getArticleIdForSocialMetadataOps({
            id: articleId,
            primaryArticleId,
        });

        // if the primary article id is the same as the article id,
        // then there is no need to update the primary article's metadata since it's already updated
        // by another saga.
        const isPrimaryArticleSocialMetaAlreadyUpdated = articleIdForSocialMetaOps === articleId;
        if (isPrimaryArticleSocialMetaAlreadyUpdated) {
            continue;
        }

        // This is a continuation article, update the primary article's metadata as well
        const articleSocialMetaUpdate = articleSocialMetaUpdates.find(
            ({ articleId: articleSocialMetaId }) => articleSocialMetaId === articleId,
        )?.socialMetadataUpdate;

        if (!articleSocialMetaUpdate) {
            error(`Social metadata update is missing for the article id: ${articleId}`);
            continue;
        }

        socialMetadataForPrimaryArticleUpdates.push({
            articleId: articleIdForSocialMetaOps,
            socialMetadataUpdate: articleSocialMetaUpdate,
        });
    }

    if (!socialMetadataForPrimaryArticleUpdates.length) {
        return;
    }

    yield put(
        articleSocialMetaUpdated({
            articleSocialMetaUpdates: socialMetadataForPrimaryArticleUpdates,
        }),
    );
}

export function* saga() {
    yield takeEvery(articleUpdated, onArticleUpdated);
    yield takeEvery(articlesUpdated, onMultipleArticlesUpdated);
    yield takeEvery(commentRequestAction, commentRequest);
    yield takeEvery(articleCommentsMetadataUpdated, onArticleCommentsMetadataUpdated);
    yield takeEvery(articleCommentsOpenDialogAndPublishCommentsMetadataAction, onArticleCommentsOpenDialogAndPublishCommentsMetadataAction);
    yield takeEvery(articlesLoaded, onArticlesLoaded);
    yield takeEvery(loadArticleDone, onArticleLoaded);
    yield takeEvery(articleSocialMetaUpdated, updatePrimaryArticleSocialMetadata);
}
